// client/src/components/media/ImageTextOverlay.jsx
// Canvas-based text overlay editor for adding text on top of images.
// Renders the composite image as a blob for upload.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaTimes, FaCheck, FaPlus, FaTrash, FaBold, FaItalic } from 'react-icons/fa';
import './ImageTextOverlay.css';

const DEFAULT_FONT_SIZE = 32;
const DEFAULT_COLOR = '#ffffff';
const DEFAULT_BG_COLOR = 'transparent';
const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 120;

function ImageTextOverlay({ src, onSave, onClose }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });

  // Text layers - each layer has: id, text, x, y, fontSize, color, bgColor, bold, italic
  const [layers, setLayers] = useState([]);
  const [activeLayerId, setActiveLayerId] = useState(null);
  const [dragging, setDragging] = useState(null); // { layerId, offsetX, offsetY }
  const [busy, setBusy] = useState(false);

  // Scale factor between display canvas and natural image
  const [scale, setScale] = useState(1);

  // Load image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      setImgLoaded(true);
    };
    img.onerror = () => {
      console.error('Failed to load image for text overlay');
    };
    img.src = src;
  }, [src]);

  // Size canvas to fit container while preserving aspect ratio
  useEffect(() => {
    if (!imgLoaded || !imgRef.current || !containerRef.current) return;

    const img = imgRef.current;
    const container = containerRef.current;
    const maxW = container.clientWidth;
    const maxH = window.innerHeight * 0.6;

    const ratio = img.naturalWidth / img.naturalHeight;
    let w = maxW;
    let h = w / ratio;
    if (h > maxH) {
      h = maxH;
      w = h * ratio;
    }

    setCanvasSize({ w: Math.round(w), h: Math.round(h) });
    setScale(img.naturalWidth / w);
  }, [imgLoaded]);

  // Redraw canvas whenever layers or canvas size changes
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !canvasSize.w) return;

    const ctx = canvas.getContext('2d');
    canvas.width = canvasSize.w;
    canvas.height = canvasSize.h;

    ctx.drawImage(img, 0, 0, canvasSize.w, canvasSize.h);

    layers.forEach(layer => {
      const fontStyle = `${layer.italic ? 'italic ' : ''}${layer.bold ? 'bold ' : ''}${layer.fontSize}px sans-serif`;
      ctx.font = fontStyle;
      ctx.textBaseline = 'top';

      // Background behind text
      if (layer.bgColor && layer.bgColor !== 'transparent') {
        const metrics = ctx.measureText(layer.text || ' ');
        const textH = layer.fontSize * 1.3;
        ctx.fillStyle = layer.bgColor;
        ctx.fillRect(layer.x - 4, layer.y - 2, metrics.width + 8, textH + 4);
      }

      // Text stroke for readability
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.lineWidth = Math.max(2, layer.fontSize / 12);
      ctx.lineJoin = 'round';
      ctx.strokeText(layer.text || '', layer.x, layer.y);

      // Text fill
      ctx.fillStyle = layer.color;
      ctx.fillText(layer.text || '', layer.x, layer.y);

      // Selection indicator
      if (layer.id === activeLayerId) {
        const metrics = ctx.measureText(layer.text || ' ');
        const textH = layer.fontSize * 1.3;
        ctx.strokeStyle = '#ff6800';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 3]);
        ctx.strokeRect(layer.x - 6, layer.y - 4, metrics.width + 12, textH + 8);
        ctx.setLineDash([]);
      }
    });
  }, [layers, canvasSize, activeLayerId]);

  useEffect(() => { redraw(); }, [redraw]);

  // Add a new text layer
  const addLayer = () => {
    const id = Date.now();
    const newLayer = {
      id,
      text: 'Your text here',
      x: 20,
      y: 20 + layers.length * 50,
      fontSize: DEFAULT_FONT_SIZE,
      color: DEFAULT_COLOR,
      bgColor: DEFAULT_BG_COLOR,
      bold: false,
      italic: false,
    };
    setLayers(prev => [...prev, newLayer]);
    setActiveLayerId(id);
  };

  // Update a layer property
  const updateLayer = (id, key, value) => {
    setLayers(prev => prev.map(l => l.id === id ? { ...l, [key]: value } : l));
  };

  // Remove a layer
  const removeLayer = (id) => {
    setLayers(prev => prev.filter(l => l.id !== id));
    if (activeLayerId === id) setActiveLayerId(null);
  };

  // Find which layer is under a point
  const hitTest = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');

    // Check layers in reverse order (top-most first)
    for (let i = layers.length - 1; i >= 0; i--) {
      const layer = layers[i];
      const fontStyle = `${layer.italic ? 'italic ' : ''}${layer.bold ? 'bold ' : ''}${layer.fontSize}px sans-serif`;
      ctx.font = fontStyle;
      const metrics = ctx.measureText(layer.text || ' ');
      const textH = layer.fontSize * 1.3;

      if (x >= layer.x - 6 && x <= layer.x + metrics.width + 6 &&
          y >= layer.y - 4 && y <= layer.y + textH + 4) {
        return layer;
      }
    }
    return null;
  };

  // Mouse/touch events for dragging
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const handlePointerDown = (e) => {
    const { x, y } = getCanvasCoords(e);
    const hit = hitTest(x, y);
    if (hit) {
      setActiveLayerId(hit.id);
      setDragging({ layerId: hit.id, offsetX: x - hit.x, offsetY: y - hit.y });
    } else {
      setActiveLayerId(null);
    }
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;
    e.preventDefault();
    const { x, y } = getCanvasCoords(e);
    updateLayer(dragging.layerId, 'x', x - dragging.offsetX);
    updateLayer(dragging.layerId, 'y', y - dragging.offsetY);
  };

  const handlePointerUp = () => {
    setDragging(null);
  };

  // Save: render at full resolution and return blob
  const handleSave = async () => {
    if (!imgRef.current || layers.length === 0) {
      onClose?.();
      return;
    }

    setBusy(true);
    try {
      const img = imgRef.current;
      const offscreen = document.createElement('canvas');
      offscreen.width = img.naturalWidth;
      offscreen.height = img.naturalHeight;
      const ctx = offscreen.getContext('2d');

      ctx.drawImage(img, 0, 0);

      // Draw layers at full resolution
      layers.forEach(layer => {
        const scaledFontSize = Math.round(layer.fontSize * scale);
        const scaledX = Math.round(layer.x * scale);
        const scaledY = Math.round(layer.y * scale);

        const fontStyle = `${layer.italic ? 'italic ' : ''}${layer.bold ? 'bold ' : ''}${scaledFontSize}px sans-serif`;
        ctx.font = fontStyle;
        ctx.textBaseline = 'top';

        if (layer.bgColor && layer.bgColor !== 'transparent') {
          const metrics = ctx.measureText(layer.text || ' ');
          const textH = scaledFontSize * 1.3;
          ctx.fillStyle = layer.bgColor;
          ctx.fillRect(scaledX - 4 * scale, scaledY - 2 * scale, metrics.width + 8 * scale, textH + 4 * scale);
        }

        ctx.strokeStyle = 'rgba(0,0,0,0.7)';
        ctx.lineWidth = Math.max(2, scaledFontSize / 12);
        ctx.lineJoin = 'round';
        ctx.strokeText(layer.text || '', scaledX, scaledY);

        ctx.fillStyle = layer.color;
        ctx.fillText(layer.text || '', scaledX, scaledY);
      });

      const blob = await new Promise(resolve => offscreen.toBlob(resolve, 'image/jpeg', 0.92));
      onSave?.(blob);
    } catch (err) {
      console.error('Failed to render text overlay:', err);
    } finally {
      setBusy(false);
    }
  };

  const activeLayer = layers.find(l => l.id === activeLayerId);

  return (
    <div className="image-text-overlay">
      {/* Header */}
      <div className="ito-header">
        <span className="ito-title">Add Text to Image</span>
        <div className="ito-header-actions">
          <button className="ito-btn ito-btn-cancel" onClick={onClose} disabled={busy}>
            <FaTimes /> Cancel
          </button>
          <button className="ito-btn ito-btn-save" onClick={handleSave} disabled={busy || layers.length === 0}>
            <FaCheck /> {busy ? 'Saving...' : 'Apply'}
          </button>
        </div>
      </div>

      {/* Canvas area */}
      <div className="ito-canvas-container" ref={containerRef}>
        {!imgLoaded ? (
          <div className="ito-loading">Loading image...</div>
        ) : (
          <canvas
            ref={canvasRef}
            className="ito-canvas"
            style={{ width: canvasSize.w, height: canvasSize.h }}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
          />
        )}
      </div>

      {/* Controls */}
      <div className="ito-controls">
        <button className="ito-btn ito-btn-add" onClick={addLayer}>
          <FaPlus /> Add Text
        </button>

        {activeLayer && (
          <div className="ito-layer-controls">
            <div className="ito-control-row">
              <input
                type="text"
                className="ito-text-input"
                value={activeLayer.text}
                onChange={(e) => updateLayer(activeLayer.id, 'text', e.target.value)}
                placeholder="Enter text..."
              />
              <button
                className="ito-btn ito-btn-delete"
                onClick={() => removeLayer(activeLayer.id)}
                title="Remove text"
              >
                <FaTrash />
              </button>
            </div>

            <div className="ito-control-row">
              <label className="ito-control-label">Size</label>
              <input
                type="range"
                min={MIN_FONT_SIZE}
                max={MAX_FONT_SIZE}
                value={activeLayer.fontSize}
                onChange={(e) => updateLayer(activeLayer.id, 'fontSize', Number(e.target.value))}
                className="ito-range"
              />
              <span className="ito-range-value">{activeLayer.fontSize}px</span>
            </div>

            <div className="ito-control-row">
              <label className="ito-control-label">Color</label>
              <input
                type="color"
                value={activeLayer.color}
                onChange={(e) => updateLayer(activeLayer.id, 'color', e.target.value)}
                className="ito-color-picker"
              />

              <label className="ito-control-label" style={{ marginLeft: 12 }}>BG</label>
              <select
                value={activeLayer.bgColor}
                onChange={(e) => updateLayer(activeLayer.id, 'bgColor', e.target.value)}
                className="ito-select"
              >
                <option value="transparent">None</option>
                <option value="rgba(0,0,0,0.5)">Dark</option>
                <option value="rgba(255,255,255,0.7)">Light</option>
                <option value="rgba(0,0,0,0.85)">Solid Dark</option>
                <option value="#ff6800">Brand</option>
              </select>

              <button
                className={`ito-btn ito-btn-toggle ${activeLayer.bold ? 'active' : ''}`}
                onClick={() => updateLayer(activeLayer.id, 'bold', !activeLayer.bold)}
                title="Bold"
              >
                <FaBold />
              </button>
              <button
                className={`ito-btn ito-btn-toggle ${activeLayer.italic ? 'active' : ''}`}
                onClick={() => updateLayer(activeLayer.id, 'italic', !activeLayer.italic)}
                title="Italic"
              >
                <FaItalic />
              </button>
            </div>
          </div>
        )}

        {layers.length > 0 && (
          <div className="ito-hint">Drag text on the image to reposition</div>
        )}
      </div>
    </div>
  );
}

export default ImageTextOverlay;
